import {
    FC,
    FormEvent,
    ReactElement,
    useState,
} from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import classNames from 'classnames'

import { Button, InfoListItem, Logo } from '@/components'
import { Toast } from '@/compositions'
import { ConnectedToastsContainer } from '@/connectors'
import { getErrorStatus } from '@/helpers/getErrorStatus'
import { passwordRegEx } from '@/helpers/passwordRegEx'
import { trans } from '@/helpers/trans'
import { submitUserPasswordPincode } from '@/redux/authentication/authenticationActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { ToastType } from '@/types'

import './ChangePasswordPincodePage.scss'

const getFormValue = (formData: FormData, name: string): string => {
    const value = formData.get(name)

    return typeof value === 'string' ? value : ''
}

export const ChangePasswordPincodePage: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { loading, newPasswordSuccess } = useTypedSelector(state => state.authenticationReducer)
    const { error: userError } = useTypedSelector(state => state.userReducer)
    // The link in the reset password email logs the user in with a token in the url, this page is then shown standalone
    const [loginByQuery] = useState(() => new URLSearchParams(location.search).has('api_token'))
    const [passwordCheck, setPasswordCheck] = useState(true)
    const [passwordRepeatCheck, setPasswordRepeatCheck] = useState(true)
    const [pincodeCheck, setPincodeCheck] = useState(true)
    const [pincodeRepeatCheck, setPincodeRepeatCheck] = useState(true)

    const invalidToken = getErrorStatus(userError) === 401

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const formData = new FormData(event.currentTarget)
        const password = getFormValue(formData, 'password')
        const pincode = getFormValue(formData, 'pincode')

        const isPasswordValid = ! password || password.search(passwordRegEx) === 0
        const isPasswordRepeatValid = password === getFormValue(formData, 'passwordRepeat')
        const isPincodeValid = ! pincode || /^[0-9]{4}$/.test(pincode)
        const isPincodeRepeatValid = pincode === getFormValue(formData, 'pincodeRepeat')

        setPasswordCheck(isPasswordValid)
        setPasswordRepeatCheck(isPasswordRepeatValid)
        setPincodeCheck(isPincodeValid)
        setPincodeRepeatCheck(isPincodeRepeatValid)

        if (isPasswordValid && isPasswordRepeatValid && isPincodeValid && isPincodeRepeatValid && (password || pincode)) {
            dispatch(submitUserPasswordPincode(password, pincode, loginByQuery, navigate))
        }
    }

    const className = classNames('reset-password', {
        'reset-password--standalone': loginByQuery,
        'reset-password--invalid-token': invalidToken,
    })

    return (
        <div className={className}>
            {invalidToken && (
                <>
                    <div className="registration-toast-error">
                        <Toast
                            toast={{
                                type: ToastType.fail,
                                text: trans('pages.registration.expiredLink'),
                            }}
                        />
                    </div>
                    <div className="reset-password-cover" />
                </>
            )}
            {loginByQuery && (
                <div className="reset-password__logo-wrapper">
                    <Logo className="reset-password__logo" />
                </div>
            )}
            <p className="reset-password-intro">
                {trans('pages.changePasswordPincode.intro')}
            </p>
            <form className="reset-password-form" onSubmit={handleFormSubmit}>
                <ul>
                    <InfoListItem label={trans('common.password')} className="reset-password-form-group">
                        <div className="form-group reset-password-form-group">
                            <input
                                className={classNames('form-control', { 'is-invalid': ! passwordCheck })}
                                type="password"
                                id="password"
                                name="password"
                                placeholder={trans('common.password')}
                            />
                            <div className="invalid-feedback reset-password-form-requirements">
                                {trans('pages.registration.passwordRequirements')}
                            </div>
                        </div>
                    </InfoListItem>
                    <InfoListItem label={trans('pages.changePasswordPincode.confirmPassword')} className="reset-password-form-group">
                        <div className="form-group reset-password-form-group">
                            <input
                                className={classNames('form-control', { 'is-invalid': ! passwordRepeatCheck })}
                                type="password"
                                id="passwordRepeat"
                                name="passwordRepeat"
                                placeholder={trans('pages.changePasswordPincode.confirmPassword')}
                            />
                            <div className="invalid-feedback">
                                {trans('pages.registration.passwordNotSame')}
                            </div>
                        </div>
                    </InfoListItem>
                    <InfoListItem label={trans('common.pincode')} className="reset-password-form-group">
                        <div className="form-group reset-password-form-group">
                            <input
                                className={classNames('form-control', { 'is-invalid': ! pincodeCheck })}
                                type="password"
                                pattern="[0-9]*"
                                inputMode="numeric"
                                id="pincode"
                                name="pincode"
                                placeholder={trans('common.pincode')}
                            />
                            <div className="invalid-feedback reset-password-form-requirements">
                                {trans('pages.registration.pincodeRequirements')}
                            </div>
                        </div>
                    </InfoListItem>
                    <InfoListItem label={trans('pages.changePasswordPincode.confirmPincode')} className="reset-password-form-group">
                        <div className="form-group reset-password-form-group">
                            <input
                                className={classNames('form-control', { 'is-invalid': ! pincodeRepeatCheck })}
                                type="password"
                                pattern="[0-9]*"
                                inputMode="numeric"
                                id="pincodeRepeat"
                                name="pincodeRepeat"
                                placeholder={trans('pages.changePasswordPincode.confirmPincode')}
                            />
                            <div className="invalid-feedback">
                                {trans('pages.registration.pincodeNotSame')}
                            </div>
                        </div>
                    </InfoListItem>
                </ul>
                <div className="reset-password-bottom">
                    {newPasswordSuccess && loginByQuery ? (
                        <NavLink to="/login" className="reset-password__back-to-login-button">
                            {trans('pages.login.backToLogin')}
                        </NavLink>
                    ) : (
                        <Button
                            text={trans('common.save')}
                            disabled={loading}
                            type="submit"
                            className="reset-password__save-button"
                        />
                    )}
                </div>
            </form>
            {loginByQuery && <ConnectedToastsContainer />}
        </div>
    )
}
